import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const RTOCreator = ({ isLoading }) => {
  const [rtoInput, setRtoInput] = useState('');
  const [rtos, setRtos] = useState(() => {
    const localRtos = localStorage.getItem('rtos');
    return localRtos ? JSON.parse(localRtos) : [];
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [apiAvailable, setApiAvailable] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  // Load RTOs from API and sync with localStorage
  useEffect(() => {
    const loadRtos = async () => {
      try {
        try {
          const response = await fetch('https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOTableNumbers', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setRtos(data);
            localStorage.setItem('rtos', JSON.stringify(data));
          } else {
            setApiAvailable(false);
          }
        } catch (fetchError) {
          setApiAvailable(false);
        }
      } catch (error) {
        console.error('Error loading RTOs:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };
    
    loadRtos();
  }, []);

  const handleCreateRto = async (e) => {
    if (e) e.preventDefault();
    
    const formattedInput = rtoInput.trim().split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    if (!formattedInput.trim()) {
      setErrorMessage('Please enter a location name');
      return;
    }

    if (formattedInput.length < 3) {
      setErrorMessage('Location name must be at least 3 characters');
      return;
    }

    if (rtos.some(rto => rto.RTONum.toLowerCase() === formattedInput.toLowerCase())) {
      setErrorMessage('Location already exists');
      return;
    }

    try {
      setErrorMessage('');
      
      let newRto;
      
      if (apiAvailable) {
        try {
          const response = await fetch('https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOTableNumbers', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ RTONum: formattedInput }),
          });
          
          if (response.ok) {
            newRto = await response.json();
            const updatedRtos = [...rtos, newRto];
            setRtos(updatedRtos);
            localStorage.setItem('rtos', JSON.stringify(updatedRtos));
          } else {
            throw new Error('API request failed');
          }
        } catch (apiError) {
          console.warn('API request failed, falling back to localStorage:', apiError);
          setApiAvailable(false);
        }
      }
      
      if (!apiAvailable || !newRto) {
        const timestamp = new Date().getTime();
        newRto = { 
          id: `local-${timestamp}`, 
          RTONum: formattedInput,
          createdAt: new Date().toISOString()
        };
        const updatedRtos = [...rtos, newRto];
        setRtos(updatedRtos);
        localStorage.setItem('rtos', JSON.stringify(updatedRtos));
      }
      
      setSuccessMessage(`Location "${formattedInput}" created successfully${!apiAvailable ? ' (offline mode)' : ''}!`);
      setRtoInput('');

      setTimeout(() => {
        setSuccessMessage('');
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      setErrorMessage('Failed to create location. Please try again.');
    }
  };

  const handleDeleteRto = async (rtoId, rtoName) => {
    if (!window.confirm(`Are you sure you want to delete "${rtoName}"?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      setDeletingId(rtoId);

      if (apiAvailable) {
        try {
          const response = await fetch(`https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOTableNumbers/${rtoId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (!response.ok) {
            throw new Error('API delete request failed');
          }
        } catch (apiError) {
          console.warn('API delete failed, falling back to localStorage:', apiError);
          setApiAvailable(false);
        }
      }

      const updatedRtos = rtos.filter(rto => rto.id !== rtoId);
      setRtos(updatedRtos);
      localStorage.setItem('rtos', JSON.stringify(updatedRtos));

      setSuccessMessage(`Location "${rtoName}" deleted successfully${!apiAvailable ? ' (offline mode)' : ''}!`);
      
      setTimeout(() => {
        setSuccessMessage('');
        window.location.reload();
      }, 1000);

    } catch (error) {
      setErrorMessage('Failed to delete location. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  const handleInputChange = (e) => {
    setRtoInput(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCreateRto(e);
    }
  };

  const handleSelectRto = (rto) => {
    navigate(`/rto/${rto.RTONum}`);
  };

  return (
    <>
      <div className="card mb-4 shadow-sm m-4 mt-5">
        <div className="card-body">
          <h2 className="card-title mb-4 text-center">Create RTO</h2>
          
          <div className="row g-3 align-items-center">
            <div className="col-md-8">
              <input
                type="text"
                className="form-control"
                placeholder="Enter location name (e.g., Thanjavur, Chennai)"
                value={rtoInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
            </div>
            <div className="col-md-4">
              <button 
                onClick={handleCreateRto}
                className="btn w-100 text-white"
                disabled={isLoading}
                style={{background: 'linear-gradient(145deg, #1a2980, #26d0ce)',
}}
              >
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm me-1 text-white" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="bi bi-plus-circle me-1 text-white"></i>
                )}
                {isLoading ? 'Creating...' : 'Create RTO'}
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="alert alert-danger mt-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success mt-3">
              <i className="bi bi-check-circle-fill me-2"></i>
              {successMessage}
            </div>
          )}
        </div>
      </div>

      <div className="card shadow-sm m-4">
        <div className="card-body">
          <h2 className="card-title mb-4 text-center">Available RTOs</h2>
          
          {isInitialLoad ? (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Loading RTOs...</div>
            </div>
          ) : rtos.length === 0 ? (
            <div className="alert alert-info">No locations available. Create one above.</div>
          ) : (
            <div className="d-flex flex-wrap gap-3">
              {rtos.map((rto) => (
                <div 
                  key={rto.id || rto.RTONum} 
                  className="card shadow-sm" 
                  style={{ 
                    width: '160px',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div 
                    className="card-body text-center d-flex flex-column justify-content-between p-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelectRto(rto)}
                  >
                    <h3 
                      className="card-title mb-2" 
                      style={{ fontSize: '1rem' }}
                    >
                      {rto.RTONum}
                    </h3>
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn btn-sm btn-danger"
                        style={{ width: '80px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRto(rto.id, rto.RTONum);
                        }}
                        disabled={isDeleting && deletingId === rto.id}
                      >
                        {isDeleting && deletingId === rto.id ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <>
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RTOCreator;